import { Col, Form, Radio, Row, Select, Typography } from 'antd'
import type { OrderGroup } from '@super-mega/server/contracts/order'
import { OrderGroupSelectionField } from './order-group-selection-field.tsx'
import type { NumberOption, OrderGroupMode, ServiceOrderEditFormValues } from './service-order-form.types.ts'

export function ServiceOrderEditOwnershipFields({
  customerId,
  customerLoading,
  customerOptions,
  disabled,
  onCustomerChange,
  onCustomerSearch,
  onClearOrderGroup,
  onOpenOrderGroupSelector,
  onOrderGroupModeChange,
  onSalespersonChange,
  onSalespersonSearch,
  orderGroup,
  salespersonLoading,
  salespersonOptions
}: {
  customerId?: number
  customerLoading: boolean
  customerOptions: NumberOption[]
  disabled: boolean
  onCustomerChange: (value: number) => void
  onCustomerSearch: (value: string) => void
  onClearOrderGroup: () => void
  onOpenOrderGroupSelector: () => void
  onOrderGroupModeChange: (mode: Exclude<OrderGroupMode, 'new'>) => void
  onSalespersonChange: (value: number) => void
  onSalespersonSearch: (value: string) => void
  orderGroup?: OrderGroup
  salespersonLoading: boolean
  salespersonOptions: NumberOption[]
}) {
  const form = Form.useFormInstance<ServiceOrderEditFormValues>()
  const orderGroupMode = Form.useWatch('orderGroupMode', form) ?? 'none'
  const showExistingOrderGroup = orderGroupMode === 'existing'

  const selectOrderGroup = () => {
    if (customerId) {
      onOpenOrderGroupSelector()
    }
  }

  return (
    <section className="border-border bg-surface scroll-mt-24 rounded-xl border p-5 shadow-xs" id="section-order-info">
      <div className="mb-5">
        <Typography.Title className="mb-1!" level={5}>
          订单归属
        </Typography.Title>
        <Typography.Text type="secondary">
          待办理且未生成账单时可修改客户、业务员和订单组；切换客户会清空企业关联和订单组。
        </Typography.Text>
      </div>
      <Row gutter={[24, 0]}>
        <Col md={8} xs={24}>
          <Form.Item label="客户" name="customerId" rules={[{ message: '请选择客户', required: true }]}>
            <Select
              disabled={disabled}
              loading={customerLoading}
              onChange={onCustomerChange}
              options={customerOptions}
              placeholder="请选择客户"
              showSearch={{ onSearch: onCustomerSearch, filterOption: false }}
            />
          </Form.Item>
        </Col>
        <Col md={8} xs={24}>
          <Form.Item label="业务员（可选）" name="salespersonId">
            <Select
              disabled={disabled}
              loading={salespersonLoading}
              onChange={onSalespersonChange}
              options={salespersonOptions}
              allowClear
              placeholder="可不选择业务员"
              showSearch={{ onSearch: onSalespersonSearch, filterOption: false }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[24, 0]}>
        <Col md={8} xs={24}>
          <Form.Item
            extra="订单组不是必填项；不加入时，当前订单独立存在。"
            label="订单组（可选）"
            required={false}
            name="orderGroupMode"
            rules={[{ message: '请选择订单组方式', required: true }]}
          >
            <Radio.Group
              block
              disabled={disabled}
              onChange={(event) => onOrderGroupModeChange(event.target.value as Exclude<OrderGroupMode, 'new'>)}
              optionType="button"
              options={[
                { label: '不加入', value: 'none' },
                { label: '选择已有', value: 'existing' }
              ]}
            />
          </Form.Item>
        </Col>
        {showExistingOrderGroup ? (
          <Col md={8} xs={24}>
            <OrderGroupSelectionField
              customerId={customerId}
              disabled={disabled}
              orderGroup={orderGroup}
              onSelect={selectOrderGroup}
              onClear={onClearOrderGroup}
            />
          </Col>
        ) : null}
      </Row>
    </section>
  )
}
